import { type Car } from '@prisma/client'
import { dateToDisplayUTC } from '#app/shared/lib/strings.js'
import DetailsRowCard from './detailsRowCard'

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('es-ES', { useGrouping: true }).format(num)
}

const formatDecimal = (num: number, decimals: number): string => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: true,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num)
}

type Props = {
  car: Car
}
export default function DetailsRow({ car }: Props) {
  const details = [
    {
      imagePath: '/img/detail-row-icons/car-01 (1).svg',
      detailName: 'Detalles del vehículo',
      detailsObject: [
        { name: 'IVA deducible', value: car.deducibleIva ? 'Sí' : 'No' },
        {
          name: 'Matriculación',
          value: dateToDisplayUTC(car.matriculation) || '',
        },
        { name: 'Condición', value: car.condition },
        car.manufacturerMechanicalWarranty && {
          name: 'Garantía Mecánica del Fabricante',
          value: 'Sí',
        },
        car.manufacturerMechanicalWarranty && {
          name: 'Vencimiento Garantía Mecánica',
          value: car.manufacturerMechanicalWarrantyExpiration
            ? (dateToDisplayUTC(car.manufacturerMechanicalWarrantyExpiration) ??
              '')
            : 'N/A',
        },
        car.manufacturerBatteryWarranty && {
          name: 'Garantía de Batería del Fabricante',
          value: 'Sí',
        },
        car.manufacturerBatteryWarranty && {
          name: 'Vencimiento Garantía Batería',
          value: car.manufacturerBatteryWarrantyExpiration
            ? (dateToDisplayUTC(car.manufacturerBatteryWarrantyExpiration) ??
              '')
            : 'N/A',
        },
      ].filter(Boolean),
    },
    {
      imagePath: '/img/detail-row-icons/ruler.svg',
      detailName: 'Dimensiones',
      detailsObject: [
        { name: 'Largo', value: `${formatDecimal(car.carLength, 2)}m` },
        { name: 'Ancho', value: `${formatDecimal(car.carWidth, 2)}m` },
        { name: 'Alto', value: `${formatDecimal(car.carHeight, 2)}m` },
      ],
    },
    {
      imagePath: '/img/detail-row-icons/car-01 (2).svg',
      detailName: 'Peso y capacidad',
      detailsObject: [
        {
          name: 'Maletero',
          value: car.maxTrunkCapacity
            ? `${formatNumber(car.trunkCapacity)} a ${formatNumber(car.maxTrunkCapacity)}l`
            : `${formatNumber(car.trunkCapacity)}l`,
        },
        car.fuelTank != null &&
          car.fuelTank > 0 && {
            name: 'Deposito',
            value: `${formatNumber(car.fuelTank)} litros`,
          },
        { name: 'Peso', value: `${formatNumber(car.carWeight)} kg` },
      ].filter(Boolean),
    },
    {
      imagePath: '/img/detail-row-icons/calendar-check-02 (5).svg',
      detailName: 'Motor y transmisión',
      detailsObject: [
        {
          name: 'Potencia',
          value: `${formatNumber(car.horsePower)}cv (${formatNumber(Math.round(car.horsePower * 0.735499))}kw)`,
        },
        car.gears != null &&
          car.gears > 0 && { name: 'Marchas', value: formatNumber(car.gears) },
        car.engineDisplacement != null &&
          car.engineDisplacement > 0 && {
            name: 'Cilindrada',
            value: `${formatNumber(car.engineDisplacement)} cc`,
          },
        car.cylinderCount != null &&
          car.cylinderCount > 0 && {
            name: 'Cilindros',
            value: formatNumber(car.cylinderCount),
          },
      ].filter(Boolean),
    },
    {
      imagePath: '/img/detail-row-icons/speedometer-03.svg',
      detailName: 'Consumo y rendimiento',
      detailsObject: [
        {
          name: 'Velocidad máxima',
          value: `${formatNumber(car.maximunSpeed)} km/h`,
        },
        {
          name: 'Aceleración 0-100 km/h',
          value: `${formatDecimal(car.acceleration, 1)} s`,
        },
        car.averageConsumtion != null &&
          car.averageConsumtion > 0 && {
            name: 'Consumo mixto',
            value: `${formatDecimal(car.averageConsumtion, 1)} l/100km`,
          },
        car.roadConsumtion != null &&
          car.roadConsumtion > 0 && {
            name: 'Consumo en carretera',
            value: `${formatDecimal(car.roadConsumtion, 1)} l/100km`,
          },
        car.urbanConsumtion != null &&
          car.urbanConsumtion > 0 && {
            name: 'Consumo urbano',
            value: `${formatDecimal(car.urbanConsumtion, 1)} l/100km`,
          },
        {
          name: 'Emisión CO2',
          value: `${formatNumber(car.emissions)} gr/km`,
        },
        car.electricRange && {
          name: 'Autonomía Eléctrica',
          value: `${formatNumber(car.electricRange)} km`,
        },
      ].filter(Boolean),
    },
  ]
  return (
    <div
      className={`
        px-4

        md:px-7
      `}
    >
      {details.map((detail, i) => (
        <DetailsRowCard
          key={detail.detailName}
          detailName={detail.detailName}
          isLast={i === details.length - 1}
          imgPath={detail.imagePath}
          details={detail.detailsObject}
        />
      ))}
    </div>
  )
}
